import axios from "axios";
import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Card,
    Input,
    Row,
    Col,
    Select,
    Button,
    message,
    Modal,
    Form,
    InputNumber,
} from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from 'xlsx';

export default function FinalSalaryStatement() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const { backUrl, authData } = useContext(AuthContext);

    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        month: "",
        year: "",
        status: "",
        organisation_id: "",
        department_id: "",
        designation_id: "",
    });

    // NEW STATES
    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // Modal & Form states
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [form] = Form.useForm();
    const [dirtyFields, setDirtyFields] = useState(new Set());

    const fetchData = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/payroll/drafts/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                    "Content-Type": "application/json",
                },
            });
            const results = res.data.results || [];
            setData(results);
            setFilteredData(results);
        } catch (error) {
            console.error("Error fetching salary statement:", error);
            toast.error("Failed to fetch data");
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // 🔹 Fetch organisations on load
    useEffect(() => {
        fetchOrganisations();
    }, []);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data.results);
        } catch (err) {
            console.error("Error fetching organisations:", err);
        }
    };

    // 🔹 Fetch departments after selecting organisation
    useEffect(() => {
        if (filters.organisation_id) {
            fetchDepartments(filters.organisation_id);
            setDepartments([]);
            setDesignations([]);
            setFilters((prev) => ({ ...prev, department_id: "", designation_id: "" }));
        }
    }, [filters.organisation_id]);

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results);
        } catch (err) {
            console.error("Error fetching departments:", err);
        }
    };

    // 🔹 Fetch designations after selecting department
    useEffect(() => {
        if (filters.department_id) {
            fetchDesignations(filters.department_id);
            setDesignations([]);
            setFilters((prev) => ({ ...prev, designation_id: "" }));
        }
    }, [filters.department_id]);

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results);
        } catch (err) {
            console.error("Error fetching designations:", err);
        }
    };

    useEffect(() => {
        let filtered = [...data];

        if (filters.emp_code) {
            filtered = filtered.filter((item) =>
                item.employee?.emp_code
                    ?.toLowerCase()
                    .includes(filters.emp_code.toLowerCase())
            );
        }
        if (filters.name) {
            filtered = filtered.filter((item) => {
                const fullName = `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`.toLowerCase();
                return fullName.includes(filters.name.toLowerCase());
            });
        }

        if (filters.month) {
            filtered = filtered.filter(
                (item) => String(item.month) === String(filters.month)
            );
        }

        if (filters.year) {
            filtered = filtered.filter(
                (item) => String(item.year) === String(filters.year)
            );
        }

        if (filters.status) {
            filtered = filtered.filter(
                (item) =>
                    item.approve_status?.toLowerCase() === filters.status.toLowerCase()
            );
        }

        // NEW FILTERS
        if (filters.organisation_id) {
            filtered = filtered.filter(
                (item) =>
                    String(item.employee?.organisation_id) ===
                    String(filters.organisation_id)
            );
        }

        if (filters.department_id) {
            filtered = filtered.filter(
                (item) =>
                    String(item.employee?.department_id) ===
                    String(filters.department_id)
            );
        }

        if (filters.designation_id) {
            filtered = filtered.filter(
                (item) =>
                    String(item.employee?.designation_id) ===
                    String(filters.designation_id)
            );
        }

        setFilteredData(filtered);
    }, [filters, data]);

    const handleOpenModal = (record) => {
        setSelectedRecord(record);
        form.setFieldsValue(record);
        setDirtyFields(new Set());
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setSelectedRecord(null);
        setIsModalVisible(false);
        form.resetFields();
        setDirtyFields(new Set());
    };

    const onValuesChange = (changedValues) => {
        const updatedDirtyFields = new Set(dirtyFields);
        Object.keys(changedValues).forEach((key) => updatedDirtyFields.add(key));
        setDirtyFields(updatedDirtyFields);
    };

    const handleSubmit = async () => {
        try {
            const values = form.getFieldsValue();
            message.loading({ content: "Updating salary...", key: "modalSubmit" });

            const res = await axios.put(
                `${backUrl}/api/payroll/drafts/${selectedRecord.id}/`,
                values,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(
                res.data.message || "Salary updated successfully!"
            );
            fetchData();
            handleCloseModal();
        } catch (error) {
            console.error("Update salary error:", error);
            toast.error("Failed to update salary!");
        }
    };

    const columns = [
        {
            title: "Emp Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            fixed: "left",
        },
        {
            title: "Name",
            key: "employee_name",
            render: (record) =>
                `${record.employee?.first_name || ""} ${record.employee?.last_name || ""}`,
        },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "organisation_name", width: 200 },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "department_name", width: 200 },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "designation_name", width: 200 },
        { title: "Month Days", dataIndex: "month_days", key: "month_days" },
        { title: "Days Payable", dataIndex: "days_payable", key: "days_payable" },
        { title: "Basic", dataIndex: "basic_earned", key: "basic_earned" },
        { title: "Gross", dataIndex: "gross_earned", key: "gross_earned" },
        { title: "HRA", dataIndex: "hra_earned", key: "hra_earned" },
        { title: "Conveyance", dataIndex: "conveyance_earned", key: "conveyance_earned" },
        { title: "Other Allowances", dataIndex: "other_allowance_earned", key: "other_allowance_earned" },
        { title: "OT Days", dataIndex: "no_of_ot_days", key: "no_of_ot_days" },
        { title: "OT Hours", dataIndex: "no_of_ot_hours", key: "no_of_ot_hours" },
        { title: "OT Pay", dataIndex: "ot_pay", key: "hourly_ot_amount" },
        { title: "PT", dataIndex: "pt_pm", key: "pt" },
        { title: "Employer PF (12%)", dataIndex: "pf_employer_pm", key: "pf_employer" },
        { title: "Employer ESI (3.25%)", dataIndex: "esi_employer_pm", key: "esi_employer" },
        { title: "Employee PF", dataIndex: "pf_employee_pm", key: "pf_employee" },
        { title: "Employee ESI", dataIndex: "esi_employee_pm", key: "esi_employee" },
        { title: "Flexible Benefit Allowance", dataIndex: "flexible_benefit_allowance", key: "flexible_benefit_allowance" },
        { title: "Late days", dataIndex: "late_days", key: "late_days" },
        { title: "Late deduction", dataIndex: "late_deduction", key: "late_deduction" },
        { title: "LOP days", dataIndex: "lop_days", key: "lop_days" },
        { title: "LOP deduction", dataIndex: "lop_deduction", key: "lop_deduction" },
        { title: "Salary Advance", dataIndex: "salary_advance", key: "salary_advance" },
        { title: "Arrears", dataIndex: "arrears", key: "arrears" },
        { title: "Total CTC", dataIndex: "ctc_earned", key: "ctc_monthly" },
        { title: "Net Pay", dataIndex: "net_pay_earned", key: "net_earned" },
        { title: "Net Pay in Words", dataIndex: "net_pay_in_words", key: "net_earned" },
        { title: "Salary type", dataIndex: "salary_type", key: "salary_type" },
        { title: "Salary status", dataIndex: "salary_status", key: "salary_status" },
        { title: "CL OP", dataIndex: "cl_opening", key: "cl_opening" },
        { title: "SL OP", dataIndex: "sl_opening", key: "sl_opening" },
        { title: "EL OP", dataIndex: "el_opening", key: "el_opening" },
        { title: "CL Availed", dataIndex: "cl_availed", key: "cl_availed" },
        { title: "SL Availed", dataIndex: "sl_availed", key: "sl_availed" },
        { title: "EL Availed", dataIndex: "el_availed", key: "el_availed" },
        { title: "CL Balance", dataIndex: "cl_balance", key: "cl_balance" },
        { title: "SL Balance", dataIndex: "sl_balance", key: "sl_balance" },
        { title: "EL Balance", dataIndex: "el_balance", key: "el_balance" },
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Month", dataIndex: "salary_month", key: "salary_month" },
        { title: "Year", dataIndex: "year", key: "year" },
    ];

    const handleExportExcel = () => {
        if (!filteredData.length) {
            message.warning("No data available to export!");
            return;
        }

        // Convert table data into Excel rows
        const excelData = filteredData.map((item) => ({
            "Emp Code": item.employee?.emp_code || "",
            "Name": `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`,
            "Organisation": item.employee?.organisation_name || "",
            "Department": item.employee?.department_name || "",
            "Designation": item.employee?.designation_name || "",

            "Month Days": item.month_days || "",
            "Days Payable": item.days_payable || "",
            "Basic": item.basic_earned || "",
            "Gross": item.gross_earned || "",
            "HRA": item.hra_earned || "",
            "Conveyance": item.conveyance_earned || "",
            "Other Allowances": item.other_allowance_earned || "",
            "OT Days": item.no_of_ot_days || "",
            "OT Hours": item.no_of_ot_hours || "",
            "OT Pay": item.ot_pay || "",
            "PT": item.pt_pm || "",
            "Employer PF (12%)": item.pf_employer_pm || "",
            "Employer ESI (3.25%)": item.esi_employer_pm || "",
            "Employee PF": item.pf_employee_pm || "",
            "Employee ESI": item.esi_employee_pm || "",
            "Flexible Benefit Allowance": item.flexible_benefit_allowance || "",
            "Late days": item.late_days || "",
            "Late deduction": item.late_deduction || "",
            "LOP days": item.lop_days || "",
            "LOP deduction": item.lop_deduction || "",
            "Salary Advance": item.salary_advance || "",
            "Arrears": item.arrears || "",
            "Total CTC": item.ctc_earned || "",
            "Net Pay": item.net_pay_earned || "",
            "Net Pay in Words": item.net_pay_in_words || "",
            "Salary type": item.salary_type || "",
            "Salary status": item.salary_status || "",

            "CL OP": item.cl_opening || "",
            "SL OP": item.sl_opening || "",
            "EL OP": item.el_opening || "",
            "CL Availed": item.cl_availed || "",
            "SL Availed": item.sl_availed || "",
            "EL Availed": item.el_availed || "",
            "CL Balance": item.cl_balance || "",
            "SL Balance": item.sl_balance || "",
            "EL Balance": item.el_balance || "",

            "Date": item.date || "",
            "Month": item.salary_month || "",
            "Year": item.year || "",
        }));

        // Create worksheet + file
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salary Statement");

        XLSX.writeFile(wb, "FinalSalaryStatement.xlsx");
    };


    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
    };

    const handleBulkApprove = async () => {
        if (!selectedRowKeys.length) {
            toast.warning("Please select at least one record to approve!");
            return;
        }

        try {
            message.loading({
                content: "Approving salaries...",
                key: "bulkApprove",
            });

            const res = await axios.post(
                `${backUrl}/api/payroll/bulk-approve-salary/`,
                { ids: selectedRowKeys },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(
                res.data.message || "Selected salaries approved successfully!"
            );
            setSelectedRowKeys([]);
            fetchData();
        } catch (error) {
            console.error("Bulk approve error:", error);
            toast.error("Failed to approve selected salaries!");
        }
    };

    const handleBulkReject = async () => {
        if (!selectedRowKeys.length) {
            toast.warning("Please select at least one record to approve!");
            return;
        }

        try {
            message.loading({
                content: "Approving salaries...",
                key: "bulkApprove",
            });

            const res = await axios.post(
                `${backUrl}/api/payroll/bulk-reject-salary/`,
                { ids: selectedRowKeys },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(
                res.data.message || "Selected salaries approved successfully!"
            );
            setSelectedRowKeys([]);
            fetchData();
        } catch (error) {
            console.error("Bulk approve error:", error);
            toast.error("Failed to approve selected salaries!");
        }
    };

    return (
        <>
            <Modal
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={null}
                title={`Edit Salary – ${selectedRecord?.employee?.emp_code || ""
                    } – ${selectedRecord?.employee?.first_name || ""}`}
                width={900}
            >
                {selectedRecord && (
                    <Form
                        layout="vertical"
                        form={form}
                        onValuesChange={onValuesChange}
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={8}>
                                <Form.Item label="Month" name="month">
                                    <InputNumber style={{ width: "100%" }} disabled />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Year" name="year">
                                    <InputNumber style={{ width: "100%" }} disabled />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="Days Payable" name="days_payable">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Basic Earned" name="basic_earned">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item label="HRA Earned" name="hra_earned">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label="Conveyance Earned"
                                    name="conveyance_earned"
                                >
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    label="Other Allowance"
                                    name="other_allowance_earned"
                                >
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Gross Earned" name="gross_earned">
                                    <InputNumber style={{ width: "100%" }} disabled />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="PF Employee" name="pf_employee">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="ESI Employee" name="esi_employee">
                                    <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    label="Total Deductions"
                                    name="total_deductions"
                                >
                                    <InputNumber style={{ width: "100%" }} disabled />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item label="Net Pay" name="net_pay">
                                    <InputNumber style={{ width: "100%" }} disabled />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    label="Net Pay (in Words)"
                                    name="net_pay_in_words"
                                >
                                    <Input.TextArea rows={2} disabled />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Status" name="approve_status">
                                    <Select>
                                        <Select.Option value="Pending">
                                            Pending
                                        </Select.Option>
                                        <Select.Option value="Approved">
                                            Approved
                                        </Select.Option>
                                        <Select.Option value="Rejected">
                                            Rejected
                                        </Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <div style={{ textAlign: "right", marginTop: 20 }}>
                            <Button
                                onClick={handleCloseModal}
                                style={{ marginRight: 10 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                style={{
                                    backgroundColor: "#0e497b",
                                    borderColor: "#0e497b",
                                    fontWeight: "500",
                                }}
                            >
                                Submit
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />

            <center>
                <h5 style={{ marginTop: 20 }}>Final Salary Statement</h5>
            </center>

            {/* Filters */}
            <Card style={{ margin: 20 }}>
                <Row gutter={[16, 16]} justify="center">
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Input
                            placeholder="Filter by Emp Code"
                            value={filters.emp_code}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    emp_code: e.target.value,
                                })
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Input
                            placeholder="Filter by Name"
                            value={filters.name}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    name: e.target.value,
                                })
                            }
                        />
                    </Col>

                    {/* NEW Organisation Filter */}
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Select
                            placeholder="Filter by Organisation"
                            style={{ width: "100%" }}
                            value={filters.organisation_id || undefined}
                            onChange={(v) =>
                                setFilters({
                                    ...filters,
                                    organisation_id: v,
                                })
                            }
                            allowClear
                        >
                            {organisations.map((org) => (
                                <Select.Option key={org.id} value={org.id}>
                                    {org.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>

                    {/* NEW Department Filter */}
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Select
                            placeholder="Filter by Department"
                            style={{ width: "100%" }}
                            value={filters.department_id || undefined}
                            onChange={(v) =>
                                setFilters({
                                    ...filters,
                                    department_id: v,
                                })
                            }
                            allowClear
                            disabled={!filters.organisation_id}
                        >
                            {departments.map((dept) => (
                                <Select.Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>

                    {/* NEW Designation Filter */}
                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Select
                            placeholder="Filter by Designation"
                            style={{ width: "100%" }}
                            value={filters.designation_id || undefined}
                            onChange={(v) =>
                                setFilters({
                                    ...filters,
                                    designation_id: v,
                                })
                            }
                            allowClear
                            disabled={!filters.department_id}
                        >
                            {designations.map((des) => (
                                <Select.Option key={des.id} value={des.id}>
                                    {des.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Input
                            placeholder="Filter by Year"
                            type="number"
                            value={filters.year}
                            onChange={(e) =>
                                setFilters({
                                    ...filters,
                                    year: e.target.value,
                                })
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Select
                            placeholder="Filter by Month"
                            style={{ width: "100%" }}
                            value={filters.month || undefined}
                            onChange={(v) => setFilters({ ...filters, month: v })}
                            allowClear
                        >
                            {Array.from({ length: 12 }, (_, i) => (
                                <Select.Option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString("default", {
                                        month: "long",
                                    })}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Card style={{ margin: 20 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        marginBottom: 10,
                    }}
                >

                    <Button
                        onClick={handleExportExcel}
                        style={{
                            backgroundColor: "#0b8f2f",
                            borderColor: "#0b8f2f",
                            color: "white",
                            fontWeight: 500,
                        }}
                    >
                        Export Excel
                    </Button>

                    <Button
                        type="primary"
                        onClick={handleBulkApprove}
                        style={{
                            backgroundColor: "#0e497b",
                            borderColor: "#0e497b",
                            color: "white",
                            fontWeight: 500,
                        }}
                    >
                        Bulk Approve
                    </Button>

                    <Button
                        type="primary"
                        onClick={handleBulkReject}
                        style={{
                            backgroundColor: "#fa0505ff",
                            borderColor: "#fa0505ff",
                            color: "white",
                            fontWeight: 500,
                        }}
                    >
                        Bulk Reject
                    </Button>
                </div>

                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    bordered
                    scroll={{ x: 7000 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                        textAlign: "center",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </Card>
        </>
    );
}
